/**
 * Interactive To-Do List Component for Work Execution
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  LinearProgress,
  Alert,
  Tooltip,
  Collapse,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider
} from '@mui/material';
import {
  PlayArrow as StartIcon,
  PlayArrow,
  Pause as PauseIcon,
  CheckCircle as CompleteIcon,
  CheckCircle,
  Block as BlockIcon,
  Block,
  Warning,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Person as PersonIcon,
  Timer as TimerIcon,
  Notes as NotesIcon,
  Warning as WarningIcon,
  DragIndicator as DragIcon
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { formatDistanceToNow } from 'date-fns';

import plannedWorksService from '../../services/plannedWorksService';
import {
  WorkTodoItem,
  PlannedWorkDetail,
  TodoCompletionForm,
  getTodoStatusColor,
  TODO_STATUSES
} from '../../types/plannedWorks';

interface WorkTodoListProps {
  plannedWork: PlannedWorkDetail;
  onTodoUpdate?: (updatedTodo: WorkTodoItem) => void;
  onWorkUpdate?: (updatedWork: PlannedWorkDetail) => void;
  editable?: boolean;
  compact?: boolean;
}

interface TodoCompletionDialogProps {
  open: boolean;
  todo: WorkTodoItem | null;
  onClose: () => void;
  onComplete: (data: TodoCompletionForm) => void;
}

const TodoCompletionDialog: React.FC<TodoCompletionDialogProps> = ({
  open,
  todo,
  onClose,
  onComplete
}) => {
  const [actualDuration, setActualDuration] = useState<string>('');
  const [notes, setNotes] = useState<string>('');

  useEffect(() => {
    if (todo && open) {
      setActualDuration(todo.estimated_duration_minutes.toString());
      setNotes('');
    }
  }, [todo, open]);

  const handleSubmit = () => {
    const data: TodoCompletionForm = {
      actual_duration_minutes: actualDuration ? parseInt(actualDuration) : undefined,
      notes
    };
    onComplete(data);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Complete Task: {todo?.title}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <TextField
            label="Actual Duration (minutes)"
            type="number"
            value={actualDuration}
            onChange={(e) => setActualDuration(e.target.value)}
            helperText={`Estimated: ${todo?.estimated_duration_minutes} minutes`}
            fullWidth
          />
          
          <TextField
            label="Completion Notes"
            multiline
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any observations, issues, or notes about completing this task..."
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          startIcon={<CompleteIcon />}
        >
          Complete Task
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface TodoItemProps {
  todo: WorkTodoItem;
  index: number;
  onUpdate: (todo: WorkTodoItem) => void;
  editable: boolean;
  compact: boolean;
}

const TodoItemComponent: React.FC<TodoItemProps> = ({
  todo,
  index,
  onUpdate,
  editable,
  compact
}) => {
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [completionDialogOpen, setCompletionDialogOpen] = useState(false);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [blockReason, setBlockReason] = useState('');

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle sx={{ color: 'success.main' }} />;
      case 'in_progress':
        return <PlayArrow sx={{ color: 'primary.main' }} />;
      case 'blocked':
        return <Block sx={{ color: 'error.main' }} />;
      case 'skipped':
        return <Warning sx={{ color: 'warning.main' }} />;
      default:
        return <Checkbox edge="start" disabled={!todo.can_start} />;
    }
  };

  const handleComplete = async (data: TodoCompletionForm) => {
    try {
      setLoading(true);
      const updatedTodo = await plannedWorksService.todoItems.complete(todo.id, data);
      onUpdate(updatedTodo);
    } catch (error) {
      console.error('Failed to complete todo:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBlock = async () => {
    try {
      setLoading(true);
      const updatedTodo = await plannedWorksService.todoItems.block(todo.id, blockReason);
      onUpdate(updatedTodo);
      setBlockDialogOpen(false);
      setBlockReason('');
    } catch (error) {
      console.error('Failed to block todo:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStart = async () => {
    try {
      setLoading(true);
      const updatedTodo = await plannedWorksService.todoItems.update(todo.id, {
        status: 'in_progress'
      });
      onUpdate(updatedTodo);
    } catch (error) {
      console.error('Failed to start todo:', error);
    } finally {
      setLoading(false);
    }
  };

  const canStart = todo.can_start && todo.status === 'pending';
  const canComplete = todo.status === 'in_progress';
  const canBlock = todo.status === 'pending' || todo.status === 'in_progress';

  return (
    <>
      <Draggable draggableId={todo.id.toString()} index={index} isDragDisabled={!editable}>
        {(provided, snapshot) => (
          <Card
            ref={provided.innerRef}
            {...provided.draggableProps}
            sx={{
              mb: 1,
              opacity: todo.status === 'completed' ? 0.7 : 1,
              transform: snapshot.isDragging ? 'rotate(5deg)' : 'none',
              transition: 'transform 0.2s ease',
              '&:hover': {
                boxShadow: editable ? 2 : 1
              }
            }}
          >
            <ListItem
              sx={{
                px: 2,
                py: compact ? 1 : 2
              }}
            >
              {editable && (
                <ListItemIcon {...provided.dragHandleProps}>
                  <DragIcon />
                </ListItemIcon>
              )}
              
              <ListItemIcon>
                {getStatusIcon(todo.status)}
              </ListItemIcon>

              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      variant={compact ? "body2" : "body1"}
                      sx={{
                        textDecoration: todo.status === 'completed' ? 'line-through' : 'none',
                        fontWeight: todo.status === 'in_progress' ? 600 : 400
                      }}
                    >
                      {todo.sequence_order}. {todo.title}
                    </Typography>
                    
                    <Chip
                      label={todo.status_display}
                      color={getTodoStatusColor(todo.status)}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                }
                secondary={
                  !compact && (
                    <Stack spacing={1} sx={{ mt: 1 }}>
                      {todo.description && (
                        <Typography variant="body2" color="textSecondary">
                          {todo.description}
                        </Typography>
                      )}
                      
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        {todo.assigned_to_name && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PersonIcon fontSize="small" />
                            <Typography variant="caption">
                              {todo.assigned_to_name}
                            </Typography>
                          </Box>
                        )}
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <TimerIcon fontSize="small" />
                          <Typography variant="caption">
                            {todo.estimated_duration_minutes} min
                            {todo.actual_duration_minutes && (
                              <span> (actual: {todo.actual_duration_minutes} min)</span>
                            )}
                          </Typography>
                        </Box>
                        
                        {todo.completed_at && (
                          <Typography variant="caption" color="success.main">
                            Completed {formatDistanceToNow(new Date(todo.completed_at), { addSuffix: true })}
                          </Typography>
                        )}
                      </Box>

                      {todo.blocked_reason && (
                        <Alert severity="error" sx={{ py: 0 }}>
                          <Typography variant="caption">
                            Blocked: {todo.blocked_reason}
                          </Typography>
                        </Alert>
                      )}

                      {todo.notes && (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                          <NotesIcon fontSize="small" />
                          <Typography variant="caption" color="textSecondary">
                            {todo.notes}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  )
                }
              />

              {editable && (
                <ListItemSecondaryAction>
                  <Stack direction="row" spacing={0.5}>
                    {!compact && (
                      <Tooltip title={expanded ? "Collapse" : "Expand"}>
                        <IconButton
                          size="small"
                          onClick={() => setExpanded(!expanded)}
                        >
                          {expanded ? <CollapseIcon /> : <ExpandIcon />}
                        </IconButton>
                      </Tooltip>
                    )}
                    
                    {canStart && (
                      <Tooltip title="Start Task">
                        <IconButton
                          size="small"
                          onClick={handleStart}
                          disabled={loading}
                          color="primary"
                        >
                          <StartIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    
                    {canComplete && (
                      <Tooltip title="Complete Task">
                        <IconButton
                          size="small"
                          onClick={() => setCompletionDialogOpen(true)}
                          disabled={loading}
                          color="success"
                        >
                          <CompleteIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    
                    {canBlock && (
                      <Tooltip title="Block Task">
                        <IconButton
                          size="small"
                          onClick={() => setBlockDialogOpen(true)}
                          disabled={loading}
                          color="error"
                        >
                          <BlockIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </ListItemSecondaryAction>
              )}
            </ListItem>

            {!compact && expanded && (
              <Collapse in={expanded}>
                <CardContent sx={{ pt: 0 }}>
                  <Divider sx={{ mb: 2 }} />
                  
                  {todo.depends_on.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Dependencies:
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Waiting for tasks: {todo.depends_on.join(', ')}
                      </Typography>
                    </Box>
                  )}

                  {todo.duration_variance_minutes !== undefined && todo.duration_variance_minutes !== null && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Duration Variance:
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color={todo.duration_variance_minutes > 0 ? "error" : "success"}
                      >
                        {todo.duration_variance_minutes > 0 ? '+' : ''}{todo.duration_variance_minutes} minutes
                        {todo.duration_variance_minutes > 0 ? ' over estimate' : ' under estimate'}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Collapse>
            )}
          </Card>
        )}
      </Draggable>

      {/* Completion Dialog */}
      <TodoCompletionDialog
        open={completionDialogOpen}
        todo={todo}
        onClose={() => setCompletionDialogOpen(false)}
        onComplete={handleComplete}
      />

      {/* Block Dialog */}
      <Dialog open={blockDialogOpen} onClose={() => setBlockDialogOpen(false)}>
        <DialogTitle>Block Task</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Reason for blocking"
            multiline
            rows={3}
            value={blockReason}
            onChange={(e) => setBlockReason(e.target.value)}
            fullWidth
            sx={{ mt: 1 }}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlockDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleBlock} 
            variant="contained" 
            color="error"
            disabled={!blockReason.trim()}
            startIcon={<BlockIcon />}
          >
            Block Task
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const WorkTodoList: React.FC<WorkTodoListProps> = ({
  plannedWork,
  onTodoUpdate,
  onWorkUpdate,
  editable = true,
  compact = false
}) => {
  const [todoItems, setTodoItems] = useState<WorkTodoItem[]>(plannedWork.todo_items || []);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setTodoItems(plannedWork.todo_items || []);
  }, [plannedWork.todo_items]);

  const handleTodoUpdate = (updatedTodo: WorkTodoItem) => {
    const updatedItems = todoItems.map(item =>
      item.id === updatedTodo.id ? updatedTodo : item
    );
    setTodoItems(updatedItems);
    
    if (onTodoUpdate) {
      onTodoUpdate(updatedTodo);
    }
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination || !editable) return;

    const items = Array.from(todoItems);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update sequence orders
    const updatedItems = items.map((item, index) => ({
      ...item,
      sequence_order: index + 1
    }));

    setTodoItems(updatedItems);

    // Persist the reordering (this would require a bulk update API)
    try {
      // TODO: Implement bulk update API for reordering
      console.log('Reordering todos:', updatedItems.map(item => ({ id: item.id, sequence_order: item.sequence_order })));
    } catch (error) {
      console.error('Failed to reorder todos:', error);
      // Revert on error
      setTodoItems(todoItems);
    }
  };

  const progressStats = {
    total: todoItems.length,
    completed: todoItems.filter(item => item.status === 'completed').length,
    inProgress: todoItems.filter(item => item.status === 'in_progress').length,
    blocked: todoItems.filter(item => item.status === 'blocked').length
  };

  const progressPercentage = progressStats.total > 0 
    ? (progressStats.completed / progressStats.total) * 100 
    : 0;

  if (todoItems.length === 0) {
    return (
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No tasks defined
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Tasks will be added as the work is planned and executed.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Work Tasks ({progressStats.completed}/{progressStats.total})
          </Typography>
          
          {!compact && (
            <Stack direction="row" spacing={1}>
              {progressStats.inProgress > 0 && (
                <Chip 
                  label={`${progressStats.inProgress} in progress`} 
                  color="primary" 
                  size="small" 
                />
              )}
              {progressStats.blocked > 0 && (
                <Chip 
                  label={`${progressStats.blocked} blocked`} 
                  color="error" 
                  size="small" 
                />
              )}
            </Stack>
          )}
        </Box>

        <LinearProgress 
          variant="determinate" 
          value={progressPercentage} 
          sx={{ mb: 2, height: 8, borderRadius: 4 }}
        />

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="todo-list">
            {(provided) => (
              <List
                {...provided.droppableProps}
                ref={provided.innerRef}
                sx={{ py: 0 }}
              >
                {todoItems.map((todo, index) => (
                  <TodoItemComponent
                    key={todo.id}
                    todo={todo}
                    index={index}
                    onUpdate={handleTodoUpdate}
                    editable={editable}
                    compact={compact}
                  />
                ))}
                {provided.placeholder}
              </List>
            )}
          </Droppable>
        </DragDropContext>

        {editable && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="textSecondary">
              Drag and drop to reorder tasks
            </Typography>
            
            <Button
              variant="outlined"
              size="small"
              onClick={() => {/* Add new task logic */}}
            >
              Add Task
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
